import { networks } from '@ambire-common/consts/networks'
import type { Network } from '@ambire-common/interfaces/network'
import { getLibertyTokenByAddressAndChain } from '@ambire-common/services/libertySwap/constants'
import { ZKX_API_BASE } from '@common/config/env'

export type RailgunTokenBalance = {
  tokenAddress: string
  amount: bigint
}

export type RailgunTokenBalanceMap = Map<number, RailgunTokenBalance[]>

/**
 * Sanity cap (raw token base-units) for a single missing-POI note. Any note above this is
 * treated as corrupt and excluded from the "+$X pending POI" displays.
 *
 * Why: a malformed shielded commitment (e.g. one written on-chain by a non-RAILGUN SDK such as
 * Kohaku, which has no POI infrastructure) surfaces as a permanently-stuck MissingExternalPOI
 * note with an absurd amount (~1e38 base-units → "$656T pending POI" on the dashboard). The
 * commitment is immutable in the on-chain merkle tree, so a re-scan / DB-wipe cannot remove it;
 * the only way it leaves the UI is to filter it client-side. 1e30 sits ~7 orders above any
 * realistic legit note ($1B ETH-equiv ≈ 5e23 wei) and ~8 orders below the garbage value, so it
 * cuts the corrupt note without ever touching a real balance. Spendable notes are NOT filtered.
 */
export const MAX_SANE_POI_NOTE_AMOUNT = 10n ** 30n

/**
 * Merges a partial balance fetch (e.g. current network filter = one chain) into
 * existing data so other chains keep their last known balances on the networks screen.
 */
/**
 * Update a per-chain token-balance map with fresh data, replacing entries by chain. Used to
 * overlay a fresh fetch onto the previous wallet state per bucket (Spendable, Pending, etc.).
 */
export function mergeRailgunBalanceMaps(
  previous: RailgunTokenBalanceMap | undefined,
  incoming: RailgunTokenBalanceMap
): RailgunTokenBalanceMap {
  const merged = new Map<number, RailgunTokenBalance[]>()
  if (previous) {
    for (const [chainId, balances] of previous) {
      merged.set(Number(chainId), balances)
    }
  }
  for (const [chainId, balances] of incoming) {
    merged.set(Number(chainId), balances)
  }
  return merged
}

/**
 * Combine multiple per-chain bucket maps (e.g. Spendable + ShieldPending) into a unified
 * token list per chain, summing amounts for duplicate token addresses. Used when computing
 * `totalBalanceUSD` across buckets.
 *
 * IMPORTANT: do NOT use the per-chain-overwrite `mergeRailgunBalanceMaps` for this — it
 * silently zeroes out one bucket's tokens whenever another bucket carries an empty array
 * for the same chain (a routine occurrence after scan-Complete on chains where the wallet
 * has no pending/missing-POI UTXOs).
 */
export function combineRailgunBucketBalances(
  ...buckets: Array<RailgunTokenBalanceMap | undefined>
): RailgunTokenBalanceMap {
  const out = new Map<number, RailgunTokenBalance[]>()
  const perChain = new Map<number, Map<string, bigint>>()
  const ensure = (chainId: number) => {
    let m = perChain.get(chainId)
    if (!m) {
      m = new Map<string, bigint>()
      perChain.set(chainId, m)
    }
    return m
  }
  for (const bucket of buckets) {
    if (!bucket) continue
    for (const [chainId, balances] of bucket) {
      const cid = Number(chainId)
      const byToken = ensure(cid)
      for (const b of balances) {
        const key = b.tokenAddress.toLowerCase()
        byToken.set(key, (byToken.get(key) ?? 0n) + BigInt(b.amount))
      }
      // Ensure chain key exists even when bucket is empty so downstream Map-size checks
      // see the same chain set as the input.
      if (balances.length === 0 && !perChain.has(cid)) ensure(cid)
    }
  }
  for (const [cid, byToken] of perChain) {
    out.set(
      cid,
      Array.from(byToken, ([tokenAddress, amount]) => ({ tokenAddress, amount }))
    )
  }
  return out
}

export const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
// `${ZKX_API_BASE}/cena` — apis.zkxteam.xyz on the mobile-web DEV build,
// apis.zkxwallet.com everywhere else.
const CENA_BASE = `${ZKX_API_BASE}/cena`
const PRICE_FETCH_TIMEOUT_MS = 8000

/** Known decimals for common tokens (chainId -> address lower -> decimals). Default 18 for unknowns. */
const KNOWN_DECIMALS: Record<number, Record<string, number>> = {
  1: {
    [ZERO_ADDRESS]: 18,
    '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2': 18, // WETH
    '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48': 6, // USDC
    '0xdac17f958d2ee523a2206206994597c13d831ec7': 6, // USDT
    '0x6b175474e89094c44da98b954eedeac495271d0f': 18 // DAI
  },
  42161: {
    [ZERO_ADDRESS]: 18,
    '0x82af49447d8a07e3bd95bd0d56f35241523fbab1': 18, // WETH Arbitrum
    '0xaf88d065e77c8cc2239327c5edb3a432268e5831': 6, // USDC
    '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9': 6, // USDT
    '0xda10009cbd5d07dd0cecc66161fc93d7c9000da1': 18 // DAI
  },
  56: {
    [ZERO_ADDRESS]: 18,
    '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c': 18, // WBNB
    '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d': 18, // USDC BSC
    '0x55d398326f99059ff775485246999027b3197955': 18 // USDT BSC
  }
}

/** Known symbols for display (chainId -> address lower -> symbol). */
const KNOWN_SYMBOLS: Record<number, Record<string, string>> = {
  1: {
    [ZERO_ADDRESS]: 'ETH',
    '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2': 'WETH',
    '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48': 'USDC',
    '0xdac17f958d2ee523a2206206994597c13d831ec7': 'USDT',
    '0x6b175474e89094c44da98b954eedeac495271d0f': 'DAI'
  },
  42161: {
    [ZERO_ADDRESS]: 'ETH',
    '0x82af49447d8a07e3bd95bd0d56f35241523fbab1': 'WETH',
    '0xaf88d065e77c8cc2239327c5edb3a432268e5831': 'USDC',
    '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9': 'USDT',
    '0xda10009cbd5d07dd0cecc66161fc93d7c9000da1': 'DAI'
  },
  56: {
    [ZERO_ADDRESS]: 'BNB',
    '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c': 'WBNB',
    '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d': 'USDC',
    '0x55d398326f99059ff775485246999027b3197955': 'USDT'
  }
}

/** Wrapped base token (WETH/WBNB) per supported chain, used for base token shields. */
const WRAPPED_BASE_TOKEN_BY_CHAIN_ID: Record<number, string> = {
  1: '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2', // WETH mainnet
  42161: '0x82af49447d8a07e3bd95bd0d56f35241523fbab1', // WETH Arbitrum
  56: '0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c' // WBNB BNB Chain
  // 137: '0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270' // WMATIC Polygon — disabled
}

/**
 * Token addresses (native 0x0 + wrapped base + ERC20) that are supported for Railgun shield per chain.
 * Used to filter the shield form so only these tokens are shown.
 */
function toLowerSet(addrs: string[]): Set<string> {
  return new Set(addrs.map((a) => a.toLowerCase()))
}

const RAILGUN_SHIELD_SUPPORTED_BY_CHAIN_ID: Record<number, Set<string>> = {
  1: toLowerSet([
    ZERO_ADDRESS,
    WRAPPED_BASE_TOKEN_BY_CHAIN_ID[1]!,
    '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', // USDC
    '0xdac17f958d2ee523a2206206994597c13d831ec7', // USDT
    '0x6b175474e89094c44da98b954eedeac495271d0f' // DAI
  ]),
  42161: toLowerSet([
    ZERO_ADDRESS,
    WRAPPED_BASE_TOKEN_BY_CHAIN_ID[42161]!,
    '0xaf88d065e77c8cc2239327c5edb3a432268e5831', // USDC
    '0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9', // USDT
    '0xda10009cbd5d07dd0cecc66161fc93d7c9000da1' // DAI
  ]),
  56: toLowerSet([
    ZERO_ADDRESS,
    WRAPPED_BASE_TOKEN_BY_CHAIN_ID[56]!,
    '0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d', // USDC BSC
    '0x55d398326f99059ff775485246999027b3197955' // USDT BSC
  ])
}

export function getWrappedBaseTokenAddress(chainId: number): string | undefined {
  return WRAPPED_BASE_TOKEN_BY_CHAIN_ID[chainId]
}

export function isTokenSupportedForShield(chainId: number, tokenAddress: string): boolean {
  const set = RAILGUN_SHIELD_SUPPORTED_BY_CHAIN_ID[chainId]
  if (!set) return false
  return set.has(tokenAddress.toLowerCase())
}

export function isWrappedBaseToken(chainId: number, tokenAddress: string): boolean {
  const wrapped = WRAPPED_BASE_TOKEN_BY_CHAIN_ID[chainId]
  if (!wrapped) return false
  return wrapped.toLowerCase() === tokenAddress.toLowerCase()
}

export function getTokenDecimals(chainId: number, tokenAddress: string): number {
  const addr = tokenAddress.toLowerCase()
  const known = KNOWN_DECIMALS[chainId]?.[addr]
  if (known !== undefined) return known
  const liberty = getLibertyTokenByAddressAndChain(tokenAddress, chainId)
  if (liberty) return liberty.decimals
  return 18
}

export function getTokenSymbol(chainId: number, tokenAddress: string, network?: Network): string {
  const addr = tokenAddress.toLowerCase()
  const known = KNOWN_SYMBOLS[chainId]?.[addr]
  if (known) return known
  if (addr === ZERO_ADDRESS && network?.nativeAssetSymbol) return network.nativeAssetSymbol
  const liberty = getLibertyTokenByAddressAndChain(tokenAddress, chainId)
  if (liberty) return liberty.symbol
  return 'Token'
}

export function getTokenName(chainId: number, tokenAddress: string, network?: Network): string {
  const addr = tokenAddress.toLowerCase()
  if (addr === ZERO_ADDRESS && network?.nativeAssetName) return network.nativeAssetName
  const liberty = getLibertyTokenByAddressAndChain(tokenAddress, chainId)
  if (liberty) return liberty.name
  return getTokenSymbol(chainId, tokenAddress, network)
}

export function priceKey(chainId: number, tokenAddress: string): string {
  return `${chainId}-${tokenAddress.toLowerCase()}`
}

/**
 * Fetch USD prices for all tokens in the balance map via Cena (CoinGecko proxy).
 * Returns a map of priceKey(chainId, tokenAddress) -> usd price.
 */
export async function fetchTokenPricesForBalanceMap(
  balanceTokenMap: RailgunTokenBalanceMap
): Promise<Map<string, number>> {
  const result = new Map<string, number>()
  if (!balanceTokenMap?.size) return result

  const byChain = new Map<number, { natives: string[]; contracts: string[] }>()
  for (const [chainId, balances] of balanceTokenMap) {
    const network = networks.find((n) => n.chainId === BigInt(chainId))
    if (!network?.platformId) continue
    const natives: string[] = []
    const contracts: string[] = []
    for (const b of balances) {
      const addr = b.tokenAddress.toLowerCase()
      if (addr === ZERO_ADDRESS) natives.push(network.nativeAssetId)
      else contracts.push(addr)
    }
    if (natives.length || contracts.length) {
      byChain.set(chainId, { natives: [...new Set(natives)], contracts: [...new Set(contracts)] })
    }
  }

  const fetchWithTimeout = (url: string) =>
    Promise.race([
      fetch(url),
      new Promise<never>((_, rej) =>
        setTimeout(() => rej(new Error('price-request-timeout')), PRICE_FETCH_TIMEOUT_MS)
      )
    ])

  for (const [chainId, { natives, contracts }] of byChain) {
    const network = networks.find((n) => n.chainId === BigInt(chainId))!
    try {
      if (natives.length) {
        const url = `${CENA_BASE}/api/v3/simple/price?ids=${natives.join('%2C')}&vs_currencies=usd`
        const res = await fetchWithTimeout(url)
        const data = await res.json()
        if (res.ok && !data.error) {
          const price = Number((data[network.nativeAssetId] as { usd?: number })?.usd ?? 0)
          result.set(priceKey(chainId, ZERO_ADDRESS), price)
        }
      }
      if (contracts.length) {
        const url = `${CENA_BASE}/api/v3/simple/token_price/${
          network.platformId
        }?contract_addresses=${contracts.join('%2C')}&vs_currencies=usd`
        const res = await fetchWithTimeout(url)
        const body = await res.json()
        if (res.ok && !body.error && !body.message) {
          for (const addr of contracts) {
            const priceData = body[addr]
            const price = priceData ? Number(priceData.usd ?? 0) : 0
            result.set(priceKey(chainId, addr), price)
          }
        }
      }
    } catch {
      // Ignore errors per-chain, return what we have
    }
  }
  return result
}

/**
 * Compute total balance in USD from balanceTokenMap and a price map (from fetchTokenPricesForBalanceMap).
 */
export function getTotalBalanceUSD(
  balanceTokenMap: RailgunTokenBalanceMap,
  prices: Map<string, number>
): number {
  let total = 0
  if (!balanceTokenMap?.size || !prices?.size) return total
  for (const [chainId, balances] of balanceTokenMap) {
    for (const { tokenAddress, amount } of balances) {
      const key = priceKey(chainId, tokenAddress)
      const price = prices.get(key) ?? 0
      if (price === 0 || amount === 0n) continue
      const decimals = getTokenDecimals(chainId, tokenAddress)
      const human = Number(amount) / 10 ** decimals
      total += human * price
    }
  }
  return total
}

/**
 * Compute per-chain balance totals in USD from balanceTokenMap and a price map.
 * Returns Map(chainId -> totalUsd). Missing/unknown prices contribute 0.
 */
export function getBalanceUsdByChain(
  balanceTokenMap: RailgunTokenBalanceMap,
  prices: Map<string, number>
): Map<number, number> {
  const totals = new Map<number, number>()
  if (!balanceTokenMap?.size || !prices?.size) return totals

  for (const [chainId, balances] of balanceTokenMap) {
    let chainTotal = 0
    for (const { tokenAddress, amount } of balances) {
      const key = priceKey(chainId, tokenAddress)
      const price = prices.get(key) ?? 0
      if (price === 0 || amount === 0n) continue
      const decimals = getTokenDecimals(chainId, tokenAddress)
      const human = Number(amount) / 10 ** decimals
      chainTotal += human * price
    }
    totals.set(chainId, chainTotal)
  }

  return totals
}

/**
 * Phase 4.4 of RAILGUN_HYBRID_REWRITE_PLAN.md — surface POI status in spendable balance.
 *
 * The RAILGUN engine separates a wallet's shielded notes into three buckets:
 *   - `balance`                — fully spendable (Receive-POI present + finalized).
 *   - `missingInternalPOI`     — note exists on-chain but the engine hasn't fetched its
 *                                Receive-POI proof yet from the aggregator.
 *   - `missingExternalPOI`     — sender hasn't yet generated the POI on their end; the
 *                                holder cannot prove the note is innocent until they do.
 *
 * The dashboard / network list shows `balance` only (correct: that's what's spendable
 * right now). But users see the on-chain shielded amount on block explorers, so the
 * displayed total looks wrong unless we explicitly call out the un-spendable buckets.
 *
 * This helper computes the USD total of the missing-POI buckets per chain so the UI
 * can render a "+ \$X pending POI" subscript next to the spendable amount.
 */
export function getMissingPOIUsdByChain(
  missingInternal: RailgunTokenBalanceMap | undefined,
  missingExternal: RailgunTokenBalanceMap | undefined,
  prices: Map<string, number>
): Map<number, number> {
  const totals = new Map<number, number>()
  if (!prices?.size) return totals

  const accumulate = (map?: RailgunTokenBalanceMap) => {
    if (!map?.size) return
    for (const [chainId, balances] of map) {
      let prev = totals.get(chainId) ?? 0
      for (const { tokenAddress, amount } of balances) {
        const price = prices.get(priceKey(chainId, tokenAddress)) ?? 0
        if (price === 0 || amount === 0n || amount > MAX_SANE_POI_NOTE_AMOUNT) continue
        const decimals = getTokenDecimals(chainId, tokenAddress)
        const human = Number(amount) / 10 ** decimals
        prev += human * price
      }
      totals.set(chainId, prev)
    }
  }
  accumulate(missingInternal)
  accumulate(missingExternal)
  return totals
}

/**
 * Per-token version of {@link getMissingPOIUsdByChain}: returns a map keyed by
 * `priceKey(chainId, tokenAddress)` -> USD total across both missing-POI buckets.
 * The dashboard token list uses this to render a "+$X pending POI" subscript
 * next to each token row in private mode.
 */
export function getMissingPOIUsdByToken(
  missingInternal: RailgunTokenBalanceMap | undefined,
  missingExternal: RailgunTokenBalanceMap | undefined,
  prices: Map<string, number>
): Map<string, number> {
  const totals = new Map<string, number>()
  if (!prices?.size) return totals

  const accumulate = (map?: RailgunTokenBalanceMap) => {
    if (!map?.size) return
    for (const [chainId, balances] of map) {
      for (const { tokenAddress, amount } of balances) {
        const price = prices.get(priceKey(chainId, tokenAddress)) ?? 0
        if (price === 0 || amount === 0n || amount > MAX_SANE_POI_NOTE_AMOUNT) continue
        const decimals = getTokenDecimals(chainId, tokenAddress)
        const human = Number(amount) / 10 ** decimals
        const key = priceKey(chainId, tokenAddress)
        totals.set(key, (totals.get(key) ?? 0) + human * price)
      }
    }
  }
  accumulate(missingInternal)
  accumulate(missingExternal)
  return totals
}

/**
 * Phase 4.4 — total USD across both missing-POI buckets, summed across all chains.
 * Used by the All-networks dashboard row.
 */
export function getTotalMissingPOIUsd(
  missingInternal: RailgunTokenBalanceMap | undefined,
  missingExternal: RailgunTokenBalanceMap | undefined,
  prices: Map<string, number>
): number {
  let total = 0
  for (const v of getMissingPOIUsdByChain(missingInternal, missingExternal, prices).values()) {
    total += v
  }
  return total
}

/**
 * Fetch the current native asset USD price for a given chain.
 */
export async function fetchNativeAssetUsdPrice(chainId: number): Promise<number | null> {
  try {
    const network = networks.find((n) => n.chainId === BigInt(chainId))
    const nativeId = network?.nativeAssetId
    if (!nativeId) return null

    const url = `${CENA_BASE}/api/v3/simple/price?ids=${nativeId}&vs_currencies=usd`
    const res = await fetch(url)
    const data = await res.json()
    if (!res.ok || data.error) return null

    const price = Number((data[nativeId] as { usd?: number })?.usd ?? 0)
    return price > 0 ? price : null
  } catch {
    return null
  }
}
